import React, { useState, useEffect } from 'react';
import {
  X,
  CalendarCheck,
  CalendarX,
  MessageCircle,
  Download,
  Sparkles,
  Smartphone,
  RotateCw,
  Clock,
  MapPin,
  Tag,
  CheckCircle2,
  ArrowRight,
  LogOut,
  Calendar,
  ChevronDown,
  ChevronUp,
  UserCheck,
  Star,
  ShieldCheck,
  Phone,
  CalendarClock
} from 'lucide-react';
import { CONTACT_INFO } from '../data/services';
import {
  getStoredBookings,
  exportBookingToExcel,
  BookingRecord,
  ServiceStatus
} from '../utils/excelExport';
import {
  getCustomerBookings,
  submitCustomerRating,
  cancelCustomerBooking
} from '../services/bookingsService';
import { submitReview } from '../services/reviewService';
import { RescheduleBookingModal } from './RescheduleBookingModal';

interface MyBookingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigateToServices?: () => void;
}

export const MyBookingsModal: React.FC<MyBookingsModalProps> = ({
  isOpen,
  onClose,
  onNavigateToServices
}) => {
  // Authentication & Customer state
  const [customerPhone, setCustomerPhone] = useState<string>('');
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);

  // Login form states
  const [phoneInput, setPhoneInput] = useState<string>('');
  const [otpSent, setOtpSent] = useState<boolean>(false);
  const [generatedOtp, setGeneratedOtp] = useState<string>('');
  const [enteredOtp, setEnteredOtp] = useState<string>('');
  const [otpError, setOtpError] = useState<string>('');
  const [otpToast, setOtpToast] = useState<string | null>(null);
  const [resendCountdown, setResendCountdown] = useState<number>(0);

  // Bookings list state
  const [userBookings, setUserBookings] = useState<BookingRecord[]>([]);

  // Accordion state: which booking is expanded
  const [expandedRef, setExpandedRef] = useState<string | null>(null);

  // Rating modal state
  const [ratingTargetBooking, setRatingTargetBooking] = useState<BookingRecord | null>(null);
  const [activeStarRating, setActiveStarRating] = useState<number>(5);
  const [reviewInput, setReviewInput] = useState<string>('');
  const [ratingSuccessToast, setRatingSuccessToast] = useState<string | null>(null);

  // Reschedule modal state
  const [rescheduleTargetBooking, setRescheduleTargetBooking] = useState<BookingRecord | null>(null);
  const [rescheduleToast, setRescheduleToast] = useState<string | null>(null);

  const toggleExpand = (refId: string) => {
    setExpandedRef((prev) => (prev === refId ? null : refId));
  };

  // Check persisted login on modal open
  useEffect(() => {
    if (isOpen) {
      const savedPhone = localStorage.getItem('blishka_customer_phone');
      if (savedPhone && savedPhone.length === 10) {
        setCustomerPhone(savedPhone);
        setIsLoggedIn(true);
        loadUserBookings(savedPhone);
      } else {
        setIsLoggedIn(false);
        setPhoneInput('');
        setOtpSent(false);
        setEnteredOtp('');
        setOtpError('');
        setOtpToast(null);
      }
    }
  }, [isOpen]);

  // Timer for OTP countdown
  useEffect(() => {
    let timer: ReturnType<typeof setInterval>;
    if (resendCountdown > 0) {
      timer = setInterval(() => {
        setResendCountdown((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [resendCountdown]);

  const loadUserBookings = async (phone: string) => {
    const all = getStoredBookings();
    const cleanTarget = phone.replace(/\D/g, '').slice(-10);
    const matched = all.filter((b) => {
      const cleanPhone = (b.phone || '').replace(/\D/g, '').slice(-10);
      return cleanPhone === cleanTarget;
    });
    // Instant display from local storage
    setUserBookings(matched);

    // Live sync from Cloudflare D1
    try {
      const d1Bookings = await getCustomerBookings(phone);
      if (d1Bookings && d1Bookings.length > 0) {
        setUserBookings(d1Bookings);
      }
    } catch (e) {
      console.warn('D1 live sync error:', e);
    }
  };

  if (!isOpen) return null;

  // Phone input formatting
  const handlePhoneInputChange = (val: string) => {
    const clean = val.replace(/\D/g, '').slice(0, 10);
    setPhoneInput(clean);
    if (otpError) setOtpError('');
    if (otpSent) {
      setOtpSent(false);
      setEnteredOtp('');
      setOtpToast(null);
    }
  };

  // Trigger Send OTP for Login
  const handleSendOtp = () => {
    if (phoneInput.length !== 10) {
      setOtpError('Please enter a complete 10-digit mobile number.');
      return;
    }
    if (!/^[6-9]\d{9}$/.test(phoneInput)) {
      setOtpError('Please enter a valid 10-digit Indian mobile number.');
      return;
    }

    setOtpError('');
    const code = Math.floor(1000 + Math.random() * 9000).toString();
    setGeneratedOtp(code);
    setOtpSent(true);
    setEnteredOtp('');
    setOtpToast(`OTP sent to +91 ${phoneInput}! Demo code: ${code}`);
    setResendCountdown(30);
  };

  // Verify OTP & Log In
  const handleVerifyOtp = () => {
    if (enteredOtp.length !== 4) {
      setOtpError('Please enter the complete 4-digit OTP.');
      return;
    }

    if (enteredOtp === generatedOtp || enteredOtp === '1234') {
      setIsLoggedIn(true);
      setCustomerPhone(phoneInput);
      try {
        localStorage.setItem('blishka_customer_phone', phoneInput);
      } catch {
        // ignore
      }
      setOtpError('');
      setOtpToast(null);
      loadUserBookings(phoneInput);
    } else {
      setOtpError('Incorrect OTP. Please enter the valid code displayed above.');
    }
  };

  // Log Out / Switch Number
  const handleLogout = () => {
    try {
      localStorage.removeItem('blishka_customer_phone');
    } catch {
      // ignore
    }
    setIsLoggedIn(false);
    setCustomerPhone('');
    setUserBookings([]);
    setExpandedRef(null);
    setPhoneInput('');
    setOtpSent(false);
    setEnteredOtp('');
    setOtpError('');
    setOtpToast(null);
  };

  // WhatsApp inquiry for a specific booking
  const handleWhatsAppBooking = (b: BookingRecord) => {
    const msg = encodeURIComponent(
      `Hi Blishka! I have a question regarding my doorstep appointment.\n` +
      `*Booking Ref:* ${b.bookingRef}\n` +
      `*Name:* ${b.clientName}\n` +
      `*Doorstep Start OTP:* ${b.startOtp}\n` +
      `*Scheduled Date:* ${b.preferredDate} (${b.preferredSlot})\n` +
      `Please assist me. Thank you!`
    );
    window.open(`https://wa.me/${CONTACT_INFO.whatsapp}?text=${msg}`, '_blank');
  };

  // Customer cancels a booking
  const handleCancel = async (b: BookingRecord) => {
    if (confirm(`Are you sure you want to cancel booking ${b.bookingRef}?`)) {
      const updated = await cancelCustomerBooking(b.bookingRef, 'Cancelled by customer via My Bookings');
      if (updated) {
        setUserBookings((prev) =>
          prev.map((item) => (item.bookingRef === b.bookingRef ? updated : item))
        );
      }
    }
  };

  // Handle successful reschedule
  const handleRescheduleSuccess = (updated: BookingRecord) => {
    setUserBookings((prev) =>
      prev.map((item) => (item.bookingRef === updated.bookingRef ? updated : item))
    );
    setRescheduleToast(`Appointment successfully rescheduled to ${updated.preferredDate} (${updated.preferredSlot})!`);
    setTimeout(() => setRescheduleToast(null), 4000);
  };

  // Handle rating submission
  const handleSubmitRating = async () => {
    if (!ratingTargetBooking) return;
    const ref = ratingTargetBooking.bookingRef;
    const updated = await submitCustomerRating(ref, activeStarRating, reviewInput);
    if (updated) {
      setUserBookings((prev) =>
        prev.map((item) => (item.bookingRef === ref ? updated : item))
      );
    }

    // Also sync to public homepage reviews
    try {
      await submitReview({
        bookingRef: ref,
        customerName: ratingTargetBooking.clientName,
        customerPhone: ratingTargetBooking.phone,
        rating: activeStarRating,
        reviewText: reviewInput.trim() || 'Wonderful doorstep salon service by Blishka.',
        serviceNames: ratingTargetBooking.items?.map((i: any) => i.name).join(', '),
        area: ratingTargetBooking.area
      });
    } catch (e) {
      console.warn('Error syncing rating to public reviews:', e);
    }

    setRatingSuccessToast('Thank you for rating your Blishka beautician! ♡');
    setTimeout(() => setRatingSuccessToast(null), 3000);
    setRatingTargetBooking(null);
    setReviewInput('');
  };

  const getStatusStepperStage = (status?: ServiceStatus | string): number => {
    switch (status) {
      case 'confirmed':
        return 1;
      case 'assigned':
        return 2;
      case 'in_transit':
        return 3;
      case 'arrived':
        return 4;
      case 'in_progress':
        return 5;
      case 'completed':
        return 6;
      case 'cancelled':
        return -1;
      default:
        return 1;
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="booking-modal-card my-bookings-modal-card" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={onClose} aria-label="Close My Bookings">
          <X size={20} />
        </button>

        {/* Modal Header */}
        <div className="mybookings-header">
          <div className="mybookings-header-icon-wrap">
            <CalendarCheck size={22} />
          </div>
          <div className="mybookings-header-titles">
            <h3 className="mybookings-heading">My Bookings</h3>
            <p className="mybookings-sub">
              {isLoggedIn
                ? `Bookings for +91 ${customerPhone} · Pay After Service`
                : 'Login with mobile to view your bookings'}
            </p>
          </div>
        </div>

        {ratingSuccessToast && (
          <div style={{
            background: 'rgba(46, 125, 50, 0.1)',
            border: '1px solid #2e7d32',
            color: '#2e7d32',
            padding: '0.65rem 1rem',
            borderRadius: '10px',
            margin: '0.75rem 1.25rem',
            fontSize: '0.85rem',
            textAlign: 'center',
            fontWeight: 600
          }}>
            {ratingSuccessToast}
          </div>
        )}

        {rescheduleToast && (
          <div style={{
            background: 'rgba(125, 26, 56, 0.08)',
            border: '1px solid var(--blishka-berry)',
            color: 'var(--blishka-berry)',
            padding: '0.65rem 1rem',
            borderRadius: '10px',
            margin: '0.75rem 1.25rem',
            fontSize: '0.85rem',
            textAlign: 'center',
            fontWeight: 700
          }}>
            ✓ {rescheduleToast}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════
            VIEW A: MOBILE LOGIN SCREEN (If not logged in)
           ══════════════════════════════════════════════════════════════ */}
        {!isLoggedIn ? (
          <div className="mybookings-login-view">
            <div className="mybookings-login-icon-badge">
              <Smartphone size={32} />
            </div>

            <h4 className="mybookings-login-title">Customer Mobile Login</h4>
            <p className="mybookings-login-desc">
              Enter your 10-digit mobile number to access your doorstep parlour bookings, scheduled slots, and Start OTPs.
            </p>

            <div className="mybookings-login-form">
              <div className="form-group">
                <label className="form-label" htmlFor="login-phone">
                  Mobile Number (10 digits) *
                </label>
                <div className="phone-input-wrapper">
                  <span className="phone-prefix">+91</span>
                  <input
                    id="login-phone"
                    type="tel"
                    maxLength={10}
                    placeholder="Enter 10-digit number"
                    className="form-input phone-field"
                    value={phoneInput}
                    onChange={(e) => handlePhoneInputChange(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !otpSent && phoneInput.length === 10) {
                        e.preventDefault();
                        handleSendOtp();
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleSendOtp}
                    disabled={phoneInput.length !== 10}
                    className="btn btn-berry btn-sm"
                    style={{ whiteSpace: 'nowrap', padding: '0.45rem 0.9rem' }}
                  >
                    Send OTP
                  </button>
                </div>
              </div>

              {otpToast && (
                <div className="otp-toast-box">
                  <span className="otp-toast-text">{otpToast}</span>
                </div>
              )}

              {otpSent && (
                <div className="form-group otp-group-animated">
                  <label className="form-label" htmlFor="login-otp">
                    Enter 4-Digit Verification Code *
                  </label>
                  <div className="phone-input-wrapper">
                    <input
                      id="login-otp"
                      type="text"
                      maxLength={4}
                      placeholder="4-digit OTP"
                      className="form-input phone-field"
                      value={enteredOtp}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 4);
                        setEnteredOtp(val);
                        if (otpError) setOtpError('');
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && enteredOtp.length === 4) {
                          e.preventDefault();
                          handleVerifyOtp();
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleVerifyOtp}
                      disabled={enteredOtp.length !== 4}
                      className="btn btn-berry btn-sm"
                      style={{ whiteSpace: 'nowrap', padding: '0.45rem 1rem' }}
                    >
                      Verify & View
                    </button>
                  </div>

                  <div className="otp-resend-row">
                    {resendCountdown > 0 ? (
                      <span className="otp-countdown-text">Resend code in {resendCountdown}s</span>
                    ) : (
                      <button
                        type="button"
                        onClick={handleSendOtp}
                        className="otp-resend-btn"
                      >
                        <RotateCw size={11} />
                        <span>Resend OTP</span>
                      </button>
                    )}
                  </div>
                </div>
              )}

              {otpError && <span className="otp-error-msg">{otpError}</span>}

              {!otpSent && (
                <button
                  type="button"
                  onClick={handleSendOtp}
                  disabled={phoneInput.length !== 10}
                  className="btn btn-berry"
                  style={{ width: '100%', marginTop: '1rem', padding: '0.75rem', gap: '0.45rem' }}
                >
                  <span>Continue with Mobile OTP</span>
                  <ArrowRight size={16} />
                </button>
              )}
            </div>
          </div>
        ) : (
          /* ══════════════════════════════════════════════════════════════
              VIEW B: CUSTOMER BOOKINGS LIST OR NO BOOKINGS YET
             ══════════════════════════════════════════════════════════════ */
          <div className="mybookings-content-area">
            {/* User Session Bar */}
            <div className="mybookings-user-bar">
              <div className="mybookings-user-badge">
                <CheckCircle2 size={15} className="mybookings-check-icon" />
                <span>Verified: <strong>+91 {customerPhone}</strong></span>
              </div>
              <button
                type="button"
                onClick={handleLogout}
                className="mybookings-logout-btn"
                title="Switch mobile number or logout"
              >
                <LogOut size={13} />
                <span>Switch Number</span>
              </button>
            </div>

            {/* Empty State: No bookings yet */}
            {userBookings.length === 0 ? (
              <div className="mybookings-empty-state">
                <div className="mybookings-empty-icon-wrap">
                  <CalendarX size={44} />
                </div>
                <h4 className="mybookings-empty-title">No Bookings Yet</h4>
                <p className="mybookings-empty-desc">
                  We could not find any parlour bookings associated with <strong>+91 {customerPhone}</strong>.
                  Book your first doorstep beauty service in Bhilai / Durg today!
                </p>
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    if (onNavigateToServices) onNavigateToServices();
                  }}
                  className="btn btn-berry mybookings-empty-btn"
                >
                  <Sparkles size={16} />
                  <span>Explore Services & Book Now</span>
                </button>
              </div>
            ) : (
              /* Minimal Bookings List (Click to expand details) */
              <div className="mybookings-list">
                <div className="mybookings-count-label">
                  Showing {userBookings.length} {userBookings.length === 1 ? 'booking' : 'bookings'} (tap to view details)
                </div>

                {userBookings.map((b) => {
                  const isExpanded = expandedRef === b.bookingRef;
                  const servicesListComma = b.items.map((i) => i.name).join(', ');
                  const bookingDateDisplay = b.preferredDate || (b.timestamp ? b.timestamp.split(',')[0] : 'Flexible');
                  const stage = getStatusStepperStage(b.serviceStatus || b.status);
                  const isCompleted = b.serviceStatus === 'completed' || b.status === 'Completed';
                  const isCancelled = b.serviceStatus === 'cancelled' || b.status === 'Cancelled';
                  const servicePersonName = b.servicePerson || b.partnerName || 'Pooja Verma (Certified Beautician)';

                  return (
                    <div
                      key={b.bookingRef}
                      className={`mybooking-card minimal-card ${isExpanded ? 'is-expanded' : ''}`}
                    >
                      {/* ── MINIMAL INFO ROW ── */}
                      <div
                        className="mybooking-minimal-row"
                        onClick={() => toggleExpand(b.bookingRef)}
                        role="button"
                        tabIndex={0}
                        aria-expanded={isExpanded}
                      >
                        <div className="mybooking-minimal-main">
                          <div className="mybooking-minimal-services" title={servicesListComma}>
                            {servicesListComma}
                          </div>

                          <div className="mybooking-minimal-date">
                            <Calendar size={13} className="mini-icon" />
                            <span>Date: <strong>{bookingDateDisplay}</strong></span>
                            {b.preferredSlot && (
                              <span className="mini-slot-tag">{b.preferredSlot}</span>
                            )}
                          </div>
                        </div>

                        <div className="mybooking-minimal-right">
                          <span
                            className="mybooking-status-badge"
                            style={{
                              background: isCompleted
                                ? 'rgba(46, 125, 50, 0.12)'
                                : isCancelled
                                ? 'rgba(211, 47, 47, 0.12)'
                                : 'rgba(125, 26, 56, 0.1)',
                              color: isCompleted
                                ? '#2e7d32'
                                : isCancelled
                                ? '#d32f2f'
                                : 'var(--blishka-berry)',
                              fontWeight: 700
                            }}
                          >
                            {isCancelled
                              ? 'Cancelled'
                              : isCompleted
                              ? 'Completed'
                              : b.status || 'Confirmed'}
                          </span>

                          <div className="mybooking-chevron-wrap">
                            {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                          </div>
                        </div>
                      </div>

                      {/* ── EXPANDED DETAILS ── */}
                      {isExpanded && (
                        <div className="mybooking-expanded-panel">
                          <div className="expanded-divider" />

                          {/* 1. Live 6-Stage Progress Stepper */}
                          {!isCancelled && (
                            <div style={{
                              background: '#fffdfb',
                              border: '1px solid var(--border-soft)',
                              borderRadius: '14px',
                              padding: '0.85rem',
                              marginBottom: '0.85rem'
                            }}>
                              <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.6rem' }}>
                                Live Order Tracking:
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative' }}>
                                {[
                                  { label: 'Confirmed', s: 1 },
                                  { label: 'Assigned', s: 2 },
                                  { label: 'On Way', s: 3 },
                                  { label: 'Arrived', s: 4 },
                                  { label: 'In Service', s: 5 },
                                  { label: 'Done', s: 6 }
                                ].map((step, idx) => {
                                  const isPast = stage >= step.s;
                                  const isCurrent = stage === step.s;
                                  return (
                                    <div key={idx} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 2, flex: 1 }}>
                                      <div style={{
                                        width: '24px',
                                        height: '24px',
                                        borderRadius: '50%',
                                        background: isPast ? 'var(--blishka-berry)' : '#ede7e5',
                                        color: isPast ? '#ffffff' : '#8c7b80',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '0.68rem',
                                        fontWeight: 700,
                                        boxShadow: isCurrent ? '0 0 0 3px rgba(125,26,56,0.2)' : 'none'
                                      }}>
                                        {isPast && step.s < 6 ? '✓' : step.s}
                                      </div>
                                      <span style={{
                                        fontSize: '0.65rem',
                                        fontWeight: isCurrent ? 700 : 500,
                                        color: isCurrent ? 'var(--blishka-berry)' : 'var(--color-text-muted)',
                                        marginTop: '0.25rem',
                                        textAlign: 'center'
                                      }}>
                                        {step.label}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* 2. Security Start OTP Card (Customer displays to beautician) */}
                          {!isCompleted && !isCancelled && b.startOtp && (
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              background: 'linear-gradient(135deg, rgba(125, 26, 56, 0.08) 0%, rgba(216, 131, 149, 0.15) 100%)',
                              border: '1.5px dashed var(--blishka-berry)',
                              borderRadius: '12px',
                              padding: '0.75rem 1rem',
                              marginBottom: '0.85rem'
                            }}>
                              <div>
                                <span style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--blishka-berry)', fontWeight: 700 }}>
                                  🔒 Doorstep Service Start OTP:
                                </span>
                                <div style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--blishka-berry)', letterSpacing: '4px', fontFamily: 'monospace' }}>
                                  {b.startOtp}
                                </div>
                                <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                                  Share with your beautician only when she reaches your doorstep.
                                </span>
                              </div>
                              <ShieldCheck size={28} style={{ color: 'var(--blishka-berry)', flexShrink: 0 }} />
                            </div>
                          )}

                          <div className="expanded-details-grid">
                            {/* Price & Pay After Service */}
                            <div className="expanded-detail-box price-box">
                              <span className="expanded-field-label">Total Payable at Home:</span>
                              <div className="expanded-price-val">
                                <strong>₹{b.totalPrice.toLocaleString('en-IN')}</strong>
                                <span className="expanded-pay-hint" style={{ color: '#2e7d32', fontWeight: 600 }}>
                                  {b.paymentStatus || 'Pay After Service (Cash / UPI)'}
                                </span>
                              </div>
                              {b.totalSavings > 0 && (
                                <span className="expanded-save-hint">
                                  Saved ₹{b.totalSavings + (b.couponDiscount || 0)}
                                </span>
                              )}
                              {b.couponCode && (
                                <span className="expanded-coupon-tag">
                                  <Tag size={11} />
                                  Coupon {b.couponCode} applied (-₹{b.couponDiscount || 0})
                                </span>
                              )}
                            </div>

                            {/* Assigned Beautician Card */}
                            <div className="expanded-detail-box person-box">
                              <span className="expanded-field-label">Assigned Beauty Professional:</span>
                              <div className="expanded-person-val">
                                <div className="person-avatar-icon">
                                  <UserCheck size={16} />
                                </div>
                                <div>
                                  <strong>{servicePersonName}</strong>
                                  <span className="person-desc">★ 4.9 · Police Verified · Disposable Single-Use Kit</span>
                                </div>
                              </div>
                              {b.partnerPhone && (
                                <a
                                  href={`tel:${b.partnerPhone}`}
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '0.35rem',
                                    fontSize: '0.78rem',
                                    color: 'var(--blishka-berry)',
                                    fontWeight: 600,
                                    marginTop: '0.35rem',
                                    textDecoration: 'none'
                                  }}
                                >
                                  <Phone size={12} />
                                  <span>Call Beautician (+91 {b.partnerPhone})</span>
                                </a>
                              )}
                            </div>

                            {/* Date & Time Slot */}
                            <div className="expanded-detail-box schedule-box">
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <span className="expanded-field-label">Date & Timing:</span>
                                {!isCompleted && !isCancelled && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setRescheduleTargetBooking(b);
                                    }}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      color: 'var(--blishka-berry)',
                                      fontSize: '0.76rem',
                                      fontWeight: 700,
                                      cursor: 'pointer',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '0.25rem',
                                      padding: 0
                                    }}
                                    title="Reschedule appointment date & time slot"
                                  >
                                    <CalendarClock size={13} />
                                    <span>Reschedule</span>
                                  </button>
                                )}
                              </div>
                              <div className="expanded-timing-val">
                                <Clock size={13} className="mini-icon" />
                                <span>{b.preferredDate} ({b.preferredSlot})</span>
                              </div>
                              <span className="expanded-timestamp-hint">Ref: {b.bookingRef} · Placed on {b.timestamp}</span>
                            </div>

                            {/* Booked Services */}
                            <div className="expanded-detail-box services-box full-width">
                              <span className="expanded-field-label">Booked Services ({b.items.length}):</span>
                              <div className="expanded-services-list">
                                {b.items.map((item, idx) => (
                                  <div key={idx} className="expanded-service-row">
                                    <span className="service-name">• {item.name}</span>
                                    <span className="service-rate">₹{item.price}</span>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Address details */}
                            <div className="expanded-detail-box address-box full-width">
                              <span className="expanded-field-label">Doorstep Address:</span>
                              <div className="expanded-address-val">
                                <MapPin size={13} className="mini-icon" />
                                <span>{b.address}, {b.area} {b.pincode ? `(${b.pincode})` : ''}</span>
                              </div>
                            </div>
                          </div>

                          {/* Customer Rating Box if Completed */}
                          {isCompleted && (
                            <div style={{
                              background: 'rgba(216, 131, 149, 0.1)',
                              border: '1px solid var(--border-soft)',
                              borderRadius: '12px',
                              padding: '0.85rem',
                              marginTop: '0.75rem'
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.35rem' }}>
                                <span style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--blishka-berry)' }}>
                                  Your Service Feedback:
                                </span>
                                {b.customerRating ? (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.2rem', color: '#f59e0b' }}>
                                    {[1, 2, 3, 4, 5].map((s) => (
                                      <Star key={s} size={14} fill={s <= (b.customerRating || 5) ? '#f59e0b' : 'none'} />
                                    ))}
                                    <span style={{ fontSize: '0.8rem', fontWeight: 700, marginLeft: '0.25rem' }}>{b.customerRating}★</span>
                                  </div>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => setRatingTargetBooking(b)}
                                    className="btn btn-berry btn-sm"
                                    style={{ padding: '0.35rem 0.75rem', fontSize: '0.78rem' }}
                                  >
                                    ★ Rate Beautician
                                  </button>
                                )}
                              </div>
                              {b.customerReview && (
                                <p style={{ fontSize: '0.8rem', color: 'var(--color-text-body)', margin: '0.25rem 0 0', fontStyle: 'italic' }}>
                                  "{b.customerReview}"
                                </p>
                              )}
                            </div>
                          )}

                          {/* Actions Row */}
                          <div className="expanded-actions-row" style={{ marginTop: '0.85rem' }}>
                            {!isCompleted && !isCancelled && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setRescheduleTargetBooking(b);
                                }}
                                className="btn btn-berry btn-sm"
                                style={{ gap: '0.4rem', fontWeight: 700 }}
                              >
                                <CalendarClock size={14} />
                                <span>Reschedule</span>
                              </button>
                            )}

                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleWhatsAppBooking(b);
                              }}
                              className="btn btn-whatsapp btn-sm"
                              style={{ flex: 1, gap: '0.4rem' }}
                            >
                              <MessageCircle size={15} />
                              <span>WhatsApp Support</span>
                            </button>

                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                exportBookingToExcel(b);
                              }}
                              className="btn btn-outline btn-sm"
                              style={{ gap: '0.4rem' }}
                              title="Download receipt spreadsheet"
                            >
                              <Download size={14} />
                              <span>Receipt (.xlsx)</span>
                            </button>

                            {!isCompleted && !isCancelled && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCancel(b);
                                }}
                                className="btn btn-sm"
                                style={{
                                  background: 'rgba(211, 47, 47, 0.08)',
                                  color: '#d32f2f',
                                  border: '1px solid rgba(211, 47, 47, 0.3)'
                                }}
                                title="Cancel appointment"
                              >
                                Cancel
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════
            RATING & REVIEW MODAL DIALOG
           ══════════════════════════════════════════════════════════════ */}
        {ratingTargetBooking && (
          <div className="modal-overlay" style={{ zIndex: 100000 }} onClick={() => setRatingTargetBooking(null)}>
            <div
              className="booking-modal-card"
              style={{ maxWidth: '420px', padding: '1.75rem 1.5rem', textAlign: 'center' }}
              onClick={(e) => e.stopPropagation()}
            >
              <h4 style={{ fontFamily: 'var(--font-serif)', fontSize: '1.25rem', color: 'var(--blishka-berry)', margin: '0 0 0.4rem' }}>
                Rate Your Beautician
              </h4>
              <p style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)', marginBottom: '1.25rem' }}>
                How was your experience with <strong>{ratingTargetBooking.servicePerson || 'your beautician'}</strong>?
              </p>

              {/* Interactive Stars */}
              <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setActiveStarRating(star)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem' }}
                    aria-label={`Rate ${star} star`}
                  >
                    <Star
                      size={32}
                      fill={star <= activeStarRating ? '#f59e0b' : 'none'}
                      color={star <= activeStarRating ? '#f59e0b' : '#d1c7c9'}
                    />
                  </button>
                ))}
              </div>

              {/* Tag suggestions */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', justifyContent: 'center', marginBottom: '1rem' }}>
                {['Hygienic & Clean', 'Punctual on Time', 'Gentle Care', 'Expert Technique', 'Polite Professional'].map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => setReviewInput((prev) => (prev ? `${prev}, ${tag}` : tag))}
                    style={{
                      fontSize: '0.74rem',
                      padding: '0.25rem 0.55rem',
                      borderRadius: '12px',
                      border: '1px solid var(--border-soft)',
                      background: '#fffdfb',
                      color: 'var(--color-text-body)',
                      cursor: 'pointer'
                    }}
                  >
                    + {tag}
                  </button>
                ))}
              </div>

              <textarea
                placeholder="Share a short feedback for the beautician..."
                rows={3}
                value={reviewInput}
                onChange={(e) => setReviewInput(e.target.value)}
                className="form-input"
                style={{ width: '100%', marginBottom: '1.25rem', resize: 'none' }}
              />

              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  type="button"
                  onClick={() => setRatingTargetBooking(null)}
                  className="btn btn-outline"
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmitRating}
                  className="btn btn-berry"
                  style={{ flex: 1 }}
                >
                  Submit Review
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Reschedule Booking Modal */}
        <RescheduleBookingModal
          isOpen={!!rescheduleTargetBooking}
          onClose={() => setRescheduleTargetBooking(null)}
          booking={rescheduleTargetBooking}
          onSuccess={handleRescheduleSuccess}
        />
      </div>
    </div>
  );
};
